import { create } from "zustand";
import axios from "axios";
import { toastError } from "../services/toaster";

const API_URL = process.env.REACT_APP_API_URL;

let companyApiCalled = false;

const useAuthStore = create((set, get) => ({
  searchFilters: { name: "", fromDate: "", toDate: "" },
  setSearchFilters: (filters) => set({ searchFilters: filters }),
  token: null,
  user_id: null,
  companyId: null,
  username: null,
  user_email: null,
  department: null,
  designation: null,
  location: null,
  currency: null,
  role: null,
  withsap: 0,
  grade: null,
  employee_code: null,
  companyDetails: null,
  companyUrl: null,

  integrationToken: null,
  integrationBaseUrl: null,

  companyLoading: false,

  requestCounts: {
    all: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  },

  authCounts: {
    all: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  },

  users: [],
  usersLoading: false,
  usersLoaded: false,

  organizationLevels: [],
  organizationLoading: false,
  organizationLoaded: false,

  setToken: (newToken) => {
    set({ token: newToken });
  },

  setRequestCounts: (data) => set({ requestCounts: data }),

  setAuthCounts: (data) => set({ authCounts: data }),

  setUserData: (data) =>
    set({
      user_id: data?.user_id,
      companyId: data?.company_id,
      username: data?.username,
      user_email: data?.emailid,

      grade: data?.grade,
      department: data?.department,
      location: data?.location,

      designation: data?.designation,
      currency: data?.currency,
      role: data?.role?.toLowerCase(),

      withsap: Number(data?.withsap) || 0,
      employee_code: data?.employee_code,
    }),

  getCompanyDetails: async () => {
    try {
      const token = get().token;
      const company_id = get().companyId;

      if (!token || !company_id) return;

      if (companyApiCalled) return;

      companyApiCalled = true;

      set({ companyLoading: true });

      const response = await axios.get(
        `${API_URL}/settings/company/${company_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const company = response.data;

      set({
        companyDetails: company,
        companyUrl: company?.url,
        companyLoading: false,
      });

      return company;
    } catch (error) {
      console.error("Company API error:", error);

      if (error?.response?.status === 401) {
        toastError("Token is expired");
      }

      set({ companyLoading: false });
    }
  },

  generateIntegrationToken: async () => {
    try {
      const company = get().companyDetails;

      if (!company) return null;

      const params = new URLSearchParams();

      params.append("username", company.username);
      params.append("password", company.password);

      const baseUrl = company.url.startsWith("http")
        ? company.url
        : `https://${company.url}`;

      const res = await fetch(`${baseUrl}/api/generate_token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });

      const data = await res.json();

      if (!data?.access_token) {
        throw new Error("Token not received");
      }

      const token = data.access_token;

      set({
        integrationToken: token,
        integrationBaseUrl: baseUrl,
      });

      // Auto load APIs after token generation
      await get().getUsers();
      await get().getOrganizationLevels();

      return { token, baseUrl };
    } catch (error) {
      console.error("Token generation error", error);
      return null;
    }
  },

  getUsers: async () => {
    const { usersLoaded } = get();

    if (usersLoaded) {
      return;
    }

    try {
      const baseUrl = get().integrationBaseUrl;

      set({ usersLoading: true });

      const res = await get().Regenerated_expireToken(() =>
        fetch(`${baseUrl}/api/get_users`, {
          headers: {
            Authorization: `Bearer ${get().integrationToken}`,
          },
        }),
      );

      const data = await res.json();

      set({
        users: data?.users || [],
        usersLoading: false,
        usersLoaded: true,
      });

      return data;
    } catch (err) {
      console.error("Users API error:", err);

      set({
        usersLoading: false,
      });
    }
  },

  getOrganizationLevels: async () => {
    const { organizationLoaded } = get();

    if (organizationLoaded) {
      return;
    }

    try {
      const baseUrl = get().integrationBaseUrl;

      set({ organizationLoading: true });

      const res = await get().Regenerated_expireToken(() =>
        fetch(`${baseUrl}/api/organization_level`, {
          headers: {
            Authorization: `Bearer ${get().integrationToken}`,
          },
        }),
      );

      const data = await res.json();

      set({
        organizationLevels: data?.data || [],
        organizationLoading: false,
        organizationLoaded: true,
      });

      return data;
    } catch (err) {
      console.error("Organization level API error:", err);

      set({
        organizationLoading: false,
      });
    }
  },

  Regenerated_expireToken: async (apiCall) => {
    const executeApiCall = async () => {
      try {
        const res = await apiCall();

        if (res.status === 401) {
          throw new Error("Token Expired");
        }

        return res;
      } catch (err) {
        throw err;
      }
    };

    try {
      return await executeApiCall();
    } catch (err) {
      if (err?.message === "Token Expired") {
        console.warn(" Token expired. Regenerating...");

        const result = await get().generateIntegrationToken();

        if (!result) {
          console.error(" Token regeneration failed");
          throw new Error("Authentication failed");
        }

        await Promise.resolve();

        try {
          return await executeApiCall();
        } catch (retryErr) {
          console.error("❌ Retry failed");
          throw retryErr;
        }
      }

      throw err;
    }
  },

  formatCurrencyAmount: (amount, currencyCode = "INR") => {
    const value = Number(String(amount || 0).replace(/,/g, ""));

    if (isNaN(value)) return "";

    if (currencyCode === "INR") {
      return value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  },

  parseCurrencyAmount: (amount) => {
    return String(amount || "").replace(/,/g, "");
  },
}));

export default useAuthStore;
